
import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useElementID } from '../../utils/ElementID';
import { mapboxToken } from '../../assets/settings.json';
import keypin from '../../assets/images/keypin.png';


mapboxgl.accessToken = mapboxToken;

export interface ILatLon {
    lat: number;
    lng: number;
}

export class MapMarkerInputValue {
    static empty() {
        return new MapMarkerInputValue('', '', undefined, undefined);
    }

    static fromValue(value?: ILatLon) {
        if (value === undefined) {
            return this.empty();
        }

        return new MapMarkerInputValue(
            value.lat.toFixed(4),
            value.lng.toFixed(4),
            value.lat,
            value.lng,
            value
        );
    }

    static fromPosition(value: { lat: number, lon: number }) {
        return new MapMarkerInputValue(
            value.lat.toFixed(4),
            value.lon.toFixed(4),
            value.lat,
            value.lon,
            { lat: value.lat, lng: value.lon }
        );
    }

    latAsString: string;
    lonAsString: string;
    latAsNumber?: number;
    lonAsNumber?: number;
    lastValidPosition?: ILatLon;

    constructor(
        latAsString: string,
        lonAsString: string,
        latAsNumber?: number,
        lonAsNumber?: number,
        value?: ILatLon
    ) {
        this.lastValidPosition = value;
        this.latAsString = latAsString;
        this.lonAsString = lonAsString;
        this.latAsNumber = latAsNumber;
        this.lonAsNumber = lonAsNumber;
    }

    get valid(): boolean {
        return this.latAsNumber !== undefined && this.lonAsNumber !== undefined;
    }

    withLatValue(value: string): MapMarkerInputValue {
        if (!/^[0-9.,]+$/.test(value)) {
            return new MapMarkerInputValue(value, this.lonAsString, undefined, this.lonAsNumber, this.lastValidPosition);
        }

        const latAsNumber = parseFloat(value.replace(',', '.'));
        if (Number.isNaN(latAsNumber)) {
            return new MapMarkerInputValue(value, this.lonAsString, undefined, this.lonAsNumber, this.lastValidPosition);
        } else {
            return new MapMarkerInputValue(value, this.lonAsString, latAsNumber, this.lonAsNumber, this.lonAsNumber === undefined ? undefined : {
                lat: latAsNumber,
                lng: this.lonAsNumber
            });
        }
    }

    withLonValue(value: string): MapMarkerInputValue {
        if (!/^[0-9.,]+$/.test(value)) {
            return new MapMarkerInputValue(this.latAsString, value, this.latAsNumber, undefined, this.lastValidPosition);
        }

        const lonAsNumber = parseFloat(value.replace(',', '.'));
        if (Number.isNaN(lonAsNumber)) {
            return new MapMarkerInputValue(this.latAsString, value, this.latAsNumber, undefined, this.lastValidPosition);
        } else {
            return new MapMarkerInputValue(this.latAsString, value, this.latAsNumber, lonAsNumber, this.latAsNumber === undefined ? undefined : {
                lat: this.latAsNumber,
                lng: lonAsNumber
            });
        }
    }
}

interface MapMarkerInputProps {
    labelColumns?: number;

    label?: string;
    position: MapMarkerInputValue;
    defaultCenter?: ILatLon;
    defaultZoom?: number;
    info?: string;
    readOnly?: boolean;

    onChange: (position: MapMarkerInputValue) => void;
}

interface MapMarkerInputMapProps extends MapMarkerInputProps {
    containerElement: React.ReactElement<{ style: React.CSSProperties }>;
    mapElement: React.ReactElement<{ style: React.CSSProperties }>;
    onMapReady?: (map: mapboxgl.Map) => void;
}

export const MapMarkerInputMap = (props: MapMarkerInputMapProps) => {
    const {
        position,
        defaultCenter = position.lastValidPosition || { lat: 50.85121, lng: 2.88591 },
        defaultZoom = 8,
        onChange,
        readOnly = false,
        containerElement,
        onMapReady
    } = props;

    const mapContainer = useRef<HTMLDivElement | null>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const marker = useRef<mapboxgl.Marker | null>(null);

    useEffect(() => {
        if (map.current) return; // initialize map only once
        if (!mapContainer.current) return;

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/bitmunks/clt1iuj9x00hs01mebh1e8fgo',
            center: [defaultCenter.lng, defaultCenter.lat],
            zoom: defaultZoom
        });

        // Notify parent when map is ready
        if (onMapReady) {
            map.current.on('load', () => {
                if (map.current) {
                    onMapReady(map.current);
                }
            });
        }

        // Add click handler to place marker
        if (!readOnly) {
            map.current.on('click', (e) => {
                const { lng, lat } = e.lngLat;
                onChange(MapMarkerInputValue.fromValue({ lat, lng }));
            });
        }

        return () => {
            if (marker.current) {
                marker.current.remove();
            }
            if (map.current) {
                map.current.remove();
            }
        };
    }, []);

    // Update marker position
    useEffect(() => {
        if (!map.current) return;

        if (position.lastValidPosition) {
            if (marker.current) {
                marker.current.setLngLat([position.lastValidPosition.lng, position.lastValidPosition.lat]);
            } else {
                // Create a custom HTML element for the marker
                const el = document.createElement('div');
                el.className = 'custom-marker';
                el.style.backgroundImage = `url(${keypin})`;
                el.style.width = '40px';  // Adjust size as needed
                el.style.height = '40px'; // Adjust size as needed
                el.style.backgroundSize = 'contain';
                el.style.backgroundRepeat = 'no-repeat';
                el.style.cursor = readOnly ? 'default' : 'pointer';

                marker.current = new mapboxgl.Marker({
                    draggable: !readOnly,
                    element: el,
                    anchor: 'bottom',
                })
                    .setLngLat([position.lastValidPosition.lng, position.lastValidPosition.lat])
                    .addTo(map.current);

                if (!readOnly) {
                    marker.current.on('dragend', () => {
                        const lngLat = marker.current!.getLngLat();
                        onChange(MapMarkerInputValue.fromValue({ lat: lngLat.lat, lng: lngLat.lng }));
                    });
                }
            }
        } else {
            if (marker.current) {
                marker.current.remove();
                marker.current = null;
            }
        }
    }, [position.lastValidPosition, readOnly, onChange]);

    const containerStyle = containerElement.props.style;

    return (
        <div style={containerStyle}>
            <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
        </div>
    );
};

export const MapMarkerInput = (props: MapMarkerInputProps) => {
    const {
        labelColumns = 4,
        label,
        info,
        readOnly
    } = props;

    let labelColumnsCount = labelColumns;
    if (!label) {
        labelColumnsCount = 0;
    }
    const id = useElementID('input');
    return (
        <div className="form-group row">
            {((labelColumnsCount > 0) && <label htmlFor={id} className={`col-sm-${labelColumnsCount} control-label`}>{label}</label>)}
            <div className={`col-sm-${12 - labelColumnsCount}`}>
                <MapMarkerInputMap
                    containerElement={<div style={{ height: 300 }} />}
                    mapElement={<div style={{ height: `100%` }} />}
                    readOnly={readOnly}
                    {...props}
                />
                {info && <small className='form-text text-muted' style={{textAlign: "right"}}>{info}</small>}
            </div>
        </div>
    );
}